import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FileText, CheckCircle2, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const RTAC_LETTERS = [
  { id: 1, title: 'Confirmation of RTC and invitation to investigation meeting', category: 'Initial Investigation' },
  { id: 2, title: 'Precautionary removal from driving', category: 'Initial Investigation' },
  { id: 3, title: 'Invitation to decisions meeting', category: 'Initial Investigation' },
  { id: 4, title: 'Outcome of decisions meeting (incorporating decisions form)', category: 'Initial Investigation' },
  { id: 5, title: 'Long term removal from driving', category: 'Initial Investigation' },
  { id: 6, title: 'Reinstatement following precautionary removal from driving', category: 'Reinstatement' },
  { id: 7, title: 'Response to Blameworthy review request', category: 'Appeals' },
  { id: 8, title: 'Blameworthy decision review outcome', category: 'Appeals' },
  { id: 9, title: 'Invitation to removal from driving appeal meeting', category: 'Appeals' },
  { id: 10, title: 'Outcome of appeal meeting (incorporating decisions form)', category: 'Appeals' }
];

export default function VCRLetters({ vcrId, completedLetters = [], onLetterComplete }) {
  const [downloading, setDownloading] = useState(null);

  const handleLetterClick = async (letterId) => {
    setDownloading(letterId);
    
    try {
      // Download the Word template with VCR data
      const response = await axios.get(
        `${API}/vcr/letter-template/${letterId}?report_id=${vcrId}`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RTAC_Letter_${letterId}_${new Date().getTime()}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Letter ${letterId} downloaded - Complete in Word and mark as done`);
    } catch (error) {
      toast.error('Failed to download letter template');
    } finally {
      setDownloading(null);
    }
  };

  const markLetterComplete = async (letterId) => {
    try {
      await axios.post(
        `${API}/vcr/${vcrId}/complete-letter/${letterId}`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`Letter ${letterId} marked as completed`);
      if (onLetterComplete) onLetterComplete();
    } catch (error) {
      toast.error('Failed to mark letter as complete');
    }
  };

  const isCompleted = (letterId) => completedLetters.includes(letterId);

  // Group by category
  const categorized = {
    'Initial Investigation': RTAC_LETTERS.filter(l => l.category === 'Initial Investigation'),
    'Reinstatement': RTAC_LETTERS.filter(l => l.category === 'Reinstatement'),
    'Appeals': RTAC_LETTERS.filter(l => l.category === 'Appeals')
  };

  return (
    <Card className="border-blue-300 dark:border-slate-700" data-testid="rtac-letters-panel">
      <CardHeader>
        <CardTitle className="text-blue-700">RTAC Letter Templates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(categorized).map(([category, letters]) => (
          <div key={category}>
            <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">{category}</h4>
            <div className="space-y-2">
              {letters.map((letter) => {
                const completed = isCompleted(letter.id);
                return (
                  <div 
                    key={letter.id}
                    className={`p-4 rounded-lg border transition-all ${
                      completed ? 'bg-green-50 border-green-300' : 'bg-white border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                          completed ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-700 dark:text-slate-300'
                        }`}>
                          {letter.id}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-tight">{letter.title}</p>
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleLetterClick(letter.id)}
                          disabled={downloading === letter.id}
                          data-testid={`download-letter-${letter.id}`}
                          className="w-full"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          {downloading === letter.id ? 'Loading...' : 'Open'}
                        </Button>
                        {!completed ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markLetterComplete(letter.id)}
                            data-testid={`complete-letter-${letter.id}`}
                            className="w-full"
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Done
                          </Button>
                        ) : (
                          <Badge variant="default" className="bg-green-600 w-full justify-center">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Complete
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        
        <div className="pt-4 border-t dark:border-slate-700">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {completedLetters.length} of 10 letters completed
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
